import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {HeaderSize, Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {PanelContentProps, PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import StockPanelFooter from '@Panels/stock-panel/components/stock-panel-footer';
import {isMobile} from 'react-device-detect';
import {clearSelectedItems} from '@Components/base-grid';
import {useAppDispatch} from '@/hooks';
import Carousel from '@Components/carousel/carousel';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import {CarouselClickBehaviour} from '@Components/carousel/carousel.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {AITextToImageBackendData} from '@Libraries/pmw-stock-media-library';
import {getGeneratedImagesForCarousel} from '@Libraries/pmw-stock-media-library';
import {getGeneratedAiVideos} from '@Libraries/ai-video.library';
import type {GenerateAISpeechResponse} from '@Libraries/text-to-speech.library';
import {getUserGeneratedSpeech} from '@Libraries/text-to-speech.library';
import {deliverStockItems, getCarouselDataForAiVideoItem, isAudioStockItem, isDesktopAudioAdd, uploadStockSelection} from '@Panels/stock-panel/stock-panel.library';
import {createGridItemDragHandler} from '@Libraries/canvas-item-drag.library';
import type {CanvasDropPosition} from '@Libraries/canvas-item-drag.library';
import {getAiImageCarouselItem, getAiVoiceCarouselItem} from '@Panels/stock-panel/stock-panel-ai-carousel.helper';
import {AiCarouselContentType, CAROUSEL_ITEM_HEIGHT} from '@Panels/stock-panel/stock-panel.types';
import {hideLoading} from '@Libraries/loading-toast-library';
import {getAssetUrl} from '@Utils/s3.util';
import type {StockAiPanelProps, StockAiPanelRow} from './stock-ai-panel.types';
import {
  AI_PANEL_ADD_LOADING_KEY,
  AI_PANEL_DEFAULT_CONTENT_TYPES,
  AI_PANEL_SKELETON_COUNT,
  AI_PANEL_SKELETON_WIDTH,
  AI_PANEL_VOICE_FETCH_LIMIT,
  STOCK_AI_PANEL_ADD_BUTTON_ID_PREFIX,
  STOCK_AI_PANEL_ID,
  STOCK_AI_PANEL_ROWS,
  STOCK_AI_PANEL_SELECTION_GROUP_ID,
} from './stock-ai-panel.types';
import styles from './stock-ai-panel.module.scss';

type ItemsByType = Partial<Record<AiCarouselContentType, CarouselItemData[]>>;

export default function StockAiPanel(props: StockAiPanelProps): ReactElement {
  const panelId = props.panelId ?? STOCK_AI_PANEL_ID;
  const enabledTypes = props.aiCarouselContentTypes ?? AI_PANEL_DEFAULT_CONTENT_TYPES;
  const [itemsByType, setItemsByType] = useState<ItemsByType>({});
  const [loaded, setLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const clickBehaviour = props.enableMultiSelection ? CarouselClickBehaviour.SELECT : CarouselClickBehaviour.ADD;

  useEffect(() => {
    dispatch(clearSelectedItems({gridSelectionGroupId: STOCK_AI_PANEL_SELECTION_GROUP_ID}));
    let cancelled = false;
    const imagesPromise: Promise<AITextToImageBackendData[]> = enabledTypes.includes(AiCarouselContentType.IMAGES) ? getGeneratedImagesForCarousel() : Promise.resolve([]);
    const videosPromise = enabledTypes.includes(AiCarouselContentType.VIDEOS) ? getGeneratedAiVideos() : Promise.resolve([]);
    const voicesPromise: Promise<GenerateAISpeechResponse[]> = enabledTypes.includes(AiCarouselContentType.AUDIO)
      ? getUserGeneratedSpeech(0, AI_PANEL_VOICE_FETCH_LIMIT)
      : Promise.resolve([]);

    const loadAiItems = async (): Promise<void> => {
      try {
        const [images, videos, voices] = await Promise.all([imagesPromise, videosPromise, voicesPromise]);
        if (cancelled) {
          return;
        }
        setItemsByType({
          [AiCarouselContentType.IMAGES]: images.map(getAiImageCarouselItem),
          [AiCarouselContentType.VIDEOS]: videos.map(getCarouselDataForAiVideoItem),
          [AiCarouselContentType.AUDIO]: voices.map((voice) => getAiVoiceCarouselItem(voice, CAROUSEL_ITEM_HEIGHT)),
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    };

    void loadAiItems();
    return (): void => {
      cancelled = true;
    };
  }, []);

  const onItemAdd = async (items: GridItemStorage[], dropPosition?: CanvasDropPosition): Promise<void> => {
    if (items.length === 0) {
      return;
    }

    try {
      const {uploadedItems} = await uploadStockSelection({
        items,
        loadingKey: AI_PANEL_ADD_LOADING_KEY,
        addInBulk: Boolean(props.addInBulk),
        onDeliver: props.onAddSelected,
        shouldLoadDataUrlImage: props.shouldLoadDataUrlImage,
        dropPosition,
      });

      // Bulk hosts (e.g. slideshow) need the whole selection in one call; the default streams each item as its upload settles.
      if (props.addInBulk && uploadedItems.length > 0) {
        await deliverStockItems(uploadedItems, props.onAddSelected);
      }
    } finally {
      // uploadStockSelection raised the toast, so it has to come down on every exit or it hangs over the panel.
      hideLoading(AI_PANEL_ADD_LOADING_KEY);
    }

    if (isDesktopAudioAdd(items)) {
      props.onClose();
    }
  };

  const onSelectionFooterPrimaryAction = async (items: GridItemStorage[]): Promise<void> => {
    props.onClose();
    await onItemAdd(items);
  };

  const onItemPointerDown = createGridItemDragHandler<GridItemStorage>((item, dropPosition) => onItemAdd([item], dropPosition), isAudioStockItem);

  const getLoadingRow = (height: number): ReactElement => {
    return (
      <ul className={'spacing-m-0 flex-h-row spacing-p-t-4 gap-2 _overflow-hidden'}>
        {Array.from({length: AI_PANEL_SKELETON_COUNT}).map((_unused, index) => (
          <li key={index}>
            <SkeletonBrick className="radius-12" height={height} width={AI_PANEL_SKELETON_WIDTH} />
          </li>
        ))}
      </ul>
    );
  };

  const getAddButtonCarouselItem = (row: StockAiPanelRow, height: number): CarouselItemData => {
    return {
      gridItemData: {
        type: GRID_ITEM_TYPE.CUSTOM,
        id: `${STOCK_AI_PANEL_ADD_BUTTON_ID_PREFIX}${row.type}`,
        isDraggable: false,
        showHoverOverlay: false,
        onClicked: (): void => {
          props.onOpenAiTool(row.toolKey);
        },
        children: (
          <div className={styles.addButton}>
            <Icon icon="icon-plus" size={IconSize.SIZE_ICON_24} className="ct-neutral-primary" />
          </div>
        ),
      },
      width: height,
      height,
    };
  };

  const getRowCarousel = (row: StockAiPanelRow): ReactElement => {
    const height = CAROUSEL_ITEM_HEIGHT;
    const items = itemsByType[row.type];
    if (!loaded || !items) {
      return getLoadingRow(height);
    }
    return (
      <div className={styles.carouselBreakout}>
        <Carousel
          gridID={row.carouselId}
          selectionGroupId={STOCK_AI_PANEL_SELECTION_GROUP_ID}
          maxSelectionNumber={props.maxSelectionNumber}
          items={[getAddButtonCarouselItem(row, height), ...items]}
          height={height}
          clickBehaviour={clickBehaviour}
          onSingleItemClick={onItemAdd}
          isItemDraggable={props.enableDragToCanvas}
          onItemPointerDown={onItemPointerDown}
          controlButtonIconSize={IconSize.SIZE_ICON_16}
          controlButtonTopPositioning={6}
          nextBtnRightPositioning={16}
          prevBtnLeftPositioning={16}
        />
      </div>
    );
  };

  const getViewAll = (row: StockAiPanelRow, isEmpty: boolean): ReactElement | null => {
    if (isEmpty) {
      return null;
    }
    return (
      <Text
        className={styles.viewAll}
        val={window.i18next.t('pmwjs_carousel_view_all')}
        type={TextType.BODY}
        size={TextSize.XSMALL}
        bold
        onClick={(): void => {
          props.onOpenAiTool(row.toolKey);
        }}
      />
    );
  };

  const getCreateButton = (row: StockAiPanelRow): ReactElement => {
    return (
      <ClickableDiv
        className={`${styles.createButton} gap-2`}
        onClick={(): void => {
          props.onOpenAiTool(row.toolKey);
        }}
      >
        <img className={styles.createButtonImage} src={getAssetUrl(row.createImage)} alt="" />
        <Text className="ct-neutral-primary" val={window.i18next.t(row.createTextKey)} type={TextType.BODY} size={TextSize.MD} />
      </ClickableDiv>
    );
  };

  const getRowBody = (row: StockAiPanelRow, isEmpty: boolean): ReactElement => {
    if (isEmpty) {
      return getCreateButton(row);
    }
    return getRowCarousel(row);
  };

  const getRow = (row: StockAiPanelRow): ReactElement | null => {
    if (!enabledTypes.includes(row.type)) {
      return null;
    }
    const items = itemsByType[row.type];
    const isEmpty = loaded && (!items || items.length === 0);
    return (
      <li className={'spacing-p-0 flex-v-row gap-2'} key={row.type}>
        <div className={'flex-h-row flex-justify-between spacing-p-r-1'}>
          <div className={'flex-align-items-center flex-h-row gap-1'}>
            <Icon icon={row.icon} size={IconSize.SIZE_ICON_16} className="content-ai" />
            <Text className={`content-ai ${styles.headingText}`} val={window.i18next.t(row.titleKey)} type={TextType.BODY} size={TextSize.MD} bold />
          </div>
          {getViewAll(row, isEmpty)}
        </div>
        {getRowBody(row, isEmpty)}
      </li>
    );
  };

  const getHeader = (): ReactElement<PanelHeaderProps> => {
    if (isMobile) {
      return getMobileHeader();
    }
    return getDesktopHeader();
  };

  /** The same header as the stock panel's on a phone, so the back button, title and close button do not move between the two. */
  const getMobileHeader = (): ReactElement<PanelHeaderProps> => {
    return <PanelHeader titleClasses="_fit-width content-ai" title={window.i18next.t('pmwjs_ai')} showBackButton onBackButton={props.onBack} showCloseButton onClose={props.onClose} />;
  };

  const getDesktopHeader = (): ReactElement<PanelHeaderProps> => {
    return (
      <PanelHeader
        backButtonIconSize={IconSize.SIZE_ICON_16}
        titleClasses="_fit-width content-ai"
        titleTextSize={TextSize.XSMALL}
        className="_borderless spacing-p-l-4 spacing-p-t-4 spacing-m-b-4"
        size={HeaderSize.SMALL}
        title={window.i18next.t('pmwjs_ai')}
        showCloseButton={false}
        showBackButton
        onBackButton={props.onBack}
      />
    );
  };

  const getContent = (): ReactElement<PanelContentProps> => {
    return (
      <PanelContent className="flexbox flex-v-row">
        <ul className={'flex-v-row spacing-m-0 gap-6 spacing-p-t-6 spacing-p-b-4 spacing-p-l-4 spacing-p-r-4'}>{STOCK_AI_PANEL_ROWS.map(getRow)}</ul>
      </PanelContent>
    );
  };

  const getFooter = (): ReactElement<PanelFooterProps> | undefined => {
    if (!props.enableMultiSelection) {
      return undefined;
    }
    return (
      <StockPanelFooter
        selectionGroupID={STOCK_AI_PANEL_SELECTION_GROUP_ID}
        ctaText={props.selectionCtaText ?? ''}
        showCount={props.maxSelectionNumber !== 1}
        onPrimaryAction={onSelectionFooterPrimaryAction}
        makeBottomPillsScrollable={false}
      />
    );
  };

  return <Panel key={panelId} panelId={panelId} panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}
